import { useEffect, useRef, useState } from "react";

export type PhotoState = { kind: "idle" } | { kind: "busy" } | { kind: "ok"; url: string } | { kind: "error" };

/** Where the focus goes when a photo load ends: the new link, or back to the button. */
export const focusAfter = (state: PhotoState): "link" | "button" | null =>
  state.kind === "ok" ? "link" : state.kind === "error" ? "button" : null;

/** WF-23: the photo of a request. The signed URL is short-lived, so the click gets it. */
export function RequestPhoto({ load, testId }: { load: () => Promise<string>; testId: string }) {
  const [state, setState] = useState<PhotoState>({ kind: "idle" });
  const pressed = useRef(false);
  const link = useRef<HTMLAnchorElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const target = focusAfter(state);
    if (!pressed.current || target === null) return;
    pressed.current = false;
    (target === "link" ? link.current : button.current)?.focus();
  }, [state]);
  const show = async () => {
    if (state.kind === "busy") return;
    pressed.current = true;
    setState({ kind: "busy" });
    const started = performance.now();
    try {
      const url = await load();
      console.info(`request photo: loaded in ${Math.round(performance.now() - started)} ms`);
      setState({ kind: "ok", url });
    } catch (error) {
      console.warn("request photo: load failed", error);
      setState({ kind: "error" });
    }
  };
  if (state.kind === "ok") {
    return (
      <a ref={link} href={state.url} target="_blank" rel="noopener noreferrer" className="request-photo-link" data-testid={testId}>
        <img src={state.url} alt="Photo of the request" className="request-photo" />
      </a>
    );
  }
  const busy = state.kind === "busy";
  return (
    <>
      <button ref={button} type="button" className="quiet" onClick={() => void show()} aria-disabled={busy || undefined} data-testid={testId}>
        {busy ? "Loading…" : "Show photo"}
      </button>
      {state.kind === "error" && (
        <span role="alert" className="error">
          {" "}
          Cannot load the photo. Try again.
        </span>
      )}
    </>
  );
}
